import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { TabGroup, TabPanels, TabPanel } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { FaHistory } from "react-icons/fa";

// Hooks
import { useStatisticsData } from "./hooks/useStatisticsData";

// Components
import TabNavigation from "./components/TabNavigation";
import LoadingSpinner from "./components/LoadingSpinner";
import StatisticCard from "./components/StatisticCard";
import SaveSessionSection from "./components/SaveSessionSection";

// Tab Components
import BasicStatisticsTab from "./tabs/BasicStatisticsTab";
import MyStatisticsTab from "./tabs/MyStatisticsTab";
import BasicStrategiesTab from "./tabs/BasicStrategiesTab";
import MyStrategiesTab from "./tabs/MyStrategiesTab";

// Actions
import { clearHistory, clearStatistics } from "../../store/slices/dartSlice";

// Styles
import "../../styles/Statistics.css";

/**
 * Hauptkomponente für Statistics
 * Verwendet modulare Architektur mit separaten Tab-Komponenten
 * und Custom Hook für Datenmanagement
 */
const StatisticsContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State für UI
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const [showInfoBoxBasic, setShowInfoBoxBasic] = useState(true);
  const [showInfoBoxMy, setShowInfoBoxMy] = useState(true);

  // Daten aus Custom Hook
  const {
    statistics,
    basicStatistics,
    proStatistics,
    status,
    isLoading,
    user,
  } = useStatisticsData();

  const handleTabChange = (index) => {
    setSelectedIndex(index);
    setIsMobileDropdownOpen(false);
  };

  // Render-Funktion für verwischen Inhalte (Pro-Features)
  const renderBlurredTabPanel = (title) => (
    <div className="max-w-4xl mx-auto">
      <div className="relative">
        <div className="pro-feature-blur">
          <div className="grid grid-cols-1 gap-6 mb-8">
            <StatisticCard
              title={title}
              hint={t("statistics.proFeature.title")}
            >
              <div className="h-64 bg-secondary rounded-lg flex items-center justify-center">
                <p className="text-white text-xl">
                  {t("statistics.proFeature.title")}
                </p>
              </div>
            </StatisticCard>
          </div>
        </div>
        <div className="pro-feature-overlay">
          <p className="pro-feature-title">
            {t("statistics.proFeature.title")}
          </p>
          <p className="pro-feature-message">
            {t("statistics.proFeature.upgradeMessage")}
          </p>
          <button className="pro-upgrade-button">
            {t("statistics.proFeature.upgradeButton")}
          </button>
        </div>
      </div>
    </div>
  );

  // Haupt-Render
  return (
    <div className="statistics-container">
      <div className="statistics-content">
        {/* Content */}
        <div>
          {/* Loading State */}
          {isLoading && (
            <div className="loading-spinner-container">
              <LoadingSpinner />
            </div>
          )}

          {/* Statistics Content */}
          {!isLoading && status.hasStatistics && (
            <TabGroup selectedIndex={selectedIndex} onChange={setSelectedIndex}>
              <TabNavigation
                selectedIndex={selectedIndex}
                isMobileDropdownOpen={isMobileDropdownOpen}
                setIsMobileDropdownOpen={setIsMobileDropdownOpen}
                onTabChange={handleTabChange}
              />

              <TabPanels>
                {/* Tab 0: Basic Statistics */}
                <TabPanel>
                  <BasicStatisticsTab
                    basicStatistics={basicStatistics}
                    showInfoBox={showInfoBoxBasic}
                    onDismissInfoBox={() => setShowInfoBoxBasic(false)}
                    isProMember={status.isProMember}
                  />
                </TabPanel>

                {/* Tab 1: My Statistics (Pro) */}
                <TabPanel>
                  <MyStatisticsTab
                    proStatistics={proStatistics}
                    statistics={statistics}
                    showInfoBox={showInfoBoxMy}
                    onDismissInfoBox={() => setShowInfoBoxMy(false)}
                    isProMember={status.isProMember}
                    renderBlurredContent={renderBlurredTabPanel}
                  />
                </TabPanel>

                {/* Tab 2: Basic Strategies */}
                <TabPanel>
                  <BasicStrategiesTab isProMember={status.isProMember} />
                </TabPanel>

                {/* Tab 3: My Strategies (Pro) */}
                <TabPanel>
                  <MyStrategiesTab
                    proStatistics={proStatistics}
                    statistics={statistics}
                    isProMember={status.isProMember}
                    renderBlurredContent={renderBlurredTabPanel}
                  />
                </TabPanel>
              </TabPanels>
            </TabGroup>
          )}

          {/* No Statistics State */}
          {!isLoading && !status.hasStatistics && (
            <div className="no-statistics-container">
              <div className="no-statistics-card">
                <div className="mb-6">
                  <div className="no-statistics-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8 text-test"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                  <h3 className="no-statistics-title">
                    {t("statistics.noStatsAvailable")}
                  </h3>
                  <p className="no-statistics-description">
                    {t("statistics.startTrackingDescription")}
                  </p>
                </div>
                <div className="flex justify-center mt-10">
                  <button
                    onClick={() => navigate("/tracking")}
                    className="bg-test hover:bg-secondary hover:text-test text-primary px-8 py-3 rounded-lg shadow-md transition duration-200 font-medium flex items-center space-x-2 mx-auto"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    <span>{t("statistics.startTracking")}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Save Session Section */}
          {!isLoading && status.hasStatistics && (
            <SaveSessionSection
              shouldShow={
                status.evaluateIsDisabled &&
                !status.isDisabled &&
                status.isProMember
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default StatisticsContainer;
